import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, List, Skeleton, Typography } from 'antd';
import InfiniteScroll from 'react-infinite-scroll-component';
import { calculateAge } from '@medicusunion/front-utils';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { GENDERS_VALUES } from 'constants/profile';
import { UserOutlined } from '@ant-design/icons';
import { PatientRow, fetchPatients } from 'services/api/patients';

import { StyledPatientsCard } from './styled';
import { getFileUrl } from 'utils/file';

const { Title } = Typography;

const PAGE_LIMIT = 10;

interface IProps {
  onReady(): void;
}

const PatientList: FC<IProps> = ({ onReady }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const page = useRef(1);
  const timer = useRef<NodeJS.Timeout>();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<PatientRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (pageToLoad: number, term: string, append: boolean) => {
      setLoading(!append);

      try {
        const res = await fetchPatients({
          search: term || undefined,
          limit: PAGE_LIMIT,
          page: pageToLoad
        });

        setTotal(res.total);
        setResults(prev => (append ? [...prev, ...res.results] : res.results));
      } catch {
        // list keeps whatever is rendered
      } finally {
        setLoading(false);
        setTimeout(() => {
          onReady();
        });
      }
    },
    [onReady]
  );

  useEffect(() => {
    page.current = 1;
    load(1, search, false);
  }, [load, search]);

  const onSearch = (value: string) => {
    clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      setSearch(value);
    }, 350);
  };

  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    []
  );

  useEffect(() => {
    if (!id && results?.[0]?.id) {
      navigate(`${results[0].id}`);
    }
  }, [results, id, navigate]);

  const loadMoreData = () => {
    page.current = page.current + 1;
    load(page.current, search, true);
  };

  return (
    <StyledPatientsCard id="scrollableDiv">
      <InfiniteScroll
        dataLength={results.length}
        next={loadMoreData}
        hasMore={results.length < total}
        loader={
          <Skeleton
            avatar
            paragraph={{ rows: 1 }}
            active
            style={{ padding: 24 }}
          />
        }
        scrollableTarget="scrollableDiv"
      >
        <List
          itemLayout="horizontal"
          dataSource={results}
          loading={loading}
          header={
            <div className="patients-header">
              <Title level={5}>{t('patient.all_patients')}</Title>
              <div>
                <Input
                  size="large"
                  placeholder={t('common.search_by_name')}
                  onChange={e => onSearch(e.target.value)}
                />
              </div>
            </div>
          }
          renderItem={item => {
            const ageStr = item.dateOfBirth
              ? t('patient.years', {
                  num: calculateAge(item.dateOfBirth)
                })
              : '';

            const genderStr = item.gender
              ? `, ${t(`patient.${GENDERS_VALUES[item.gender]}`)}`
              : null;

            if (!item.id) {
              return null;
            }

            return (
              <Link to={`/patients/${item.id}`} key={item.id}>
                <List.Item
                  className={Number(id) === item.id ? 'active-list-item' : ''}
                >
                  <List.Item.Meta
                    avatar={
                      item.avatar ? (
                        <img
                          src={getFileUrl(item.avatar, '', 150)}
                          className="list-item-avatar"
                          alt={item.firstName || ''}
                        />
                      ) : (
                        <div className="list-item-avatar">
                          <UserOutlined
                            style={{ fontSize: 18, color: '#ffffff' }}
                          />
                        </div>
                      )
                    }
                    title={`${item.firstName} ${item.lastName}`}
                    description={
                      <p className="list-item-text">
                        {ageStr}
                        {genderStr}
                      </p>
                    }
                  />
                </List.Item>
              </Link>
            );
          }}
        />
      </InfiniteScroll>
    </StyledPatientsCard>
  );
};

export default PatientList;
